<script>
  import { line, curveCardinal } from "d3-shape";
  import { scaleLinear } from "d3-scale";
  import { extent, bisector } from "d3-array";
  import Hover from "./Hover.svelte";

  export let width;
  export let height;
  export let exploreData;
  export let maxExploreFrequency;
  export let highlightData;
  export let selectedValue;
  export let exploreGender;

  let hoverData;

  const margin = { top: 10, right: 80, bottom: 16, left: 30 };
  $: chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const xDomain = extent(exploreData[0].data, (d) => d.year);
  $: x = scaleLinear().domain(xDomain).range([0, chartWidth]);
  $: yDomain = [0, $maxExploreFrequency];
  $: y = scaleLinear().domain(yDomain).range([chartHeight, 0]);
  const xTicks = getTicks(xDomain, 5);
  $: yTicks = y.ticks(5);

  function getTicks(domain, interval) {
    const start = domain[0];
    const end = domain[1];
    let ticks = [start];
    for (let i = start + 1; i < end; i++) {
      if (i % interval === 0) ticks.push(i);
    }
    ticks.push(end);
    return ticks;
  }

  $: lineGenerator = line()
    .x((d) => x(d.year))
    .y((d) => y(d.number))
    .curve(curveCardinal.tension(0.6));

  const colors = [
    "#728CD7",
    "#4F25D2",
    "#CD35AB",
    "#F56A00",
    "#DC0018",
    "#00A2B9",
  ];

  function selectName(name) {
    if (selectedValue === undefined) {
      selectedValue = [{ value: name, label: name }];
    } else if (selectedValue.length < 6) {
      selectedValue = selectedValue.concat([{ value: name, label: name }]);
    }
  }

  let xCoord = 0;
  let hoverPoint = undefined;
  let bisect = bisector((d) => d.year).center;
  function handleMouseover(nameData) {
    hoverData = nameData;
    hoverData.data.sort((a, b) => a.year - b.year);
  }
  function handleMouseout() {
    hoverData = undefined;
    hoverPoint = undefined;
  }
  function handleMousemove(e) {
    xCoord = e.offsetX - margin.left;
    let i = bisect(hoverData.data, x.invert(xCoord));
    if (i < hoverData.data.length - 1) {
      hoverPoint = hoverData.data[i];
    } else {
      hoverPoint = undefined;
    }
  }
</script>

<svg {width} {height}>
  <g transform={`translate(${margin.left}, ${margin.top})`}>
    {#each xTicks as xTick}
      <text
        class={"axis-x-tick axis-tick"}
        x={x(xTick)}
        y={chartHeight + margin.top + 2}>{xTick}</text
      >
    {/each}
    {#each yTicks as yTick}
      <text
        class={"axis-y-tick axis-tick"}
        x={x(xDomain[0])}
        y={y(yTick)}
        dx={-4}>{yTick}</text
      >
    {/each}
    {#each exploreData as name}
      {#key name}
        <Hover let:isHovered={active}>
          <path
            class="nameline"
            id=""
            d={lineGenerator(name.data)}
            fill="none"
            stroke={active
              ? exploreGender === "girls"
                ? "#A332DE"
                : "#399D50"
              : "#ccc"}
            stroke-width={active ? 3 : 2.5}
            opacity={active ? 1 : 0.5}
            on:click={selectName(name.name)}
            on:mouseover={handleMouseover(name)}
            on:mouseout={handleMouseout}
            on:mousemove={handleMousemove}
          />
        </Hover>
      {/key}
    {/each}
    {#each highlightData as hlName, i}
      {#if hlName !== undefined}
        <path
          class="highlight-line"
          d={lineGenerator(hlName.data)}
          fill="none"
          stroke={"white"}
          stroke-width={5}
        />
        <path
          class="highlight-line"
          d={lineGenerator(hlName.data)}
          fill="none"
          stroke={colors[i]}
          stroke-width={3}
        />
        <circle
          cx={x(xDomain[1])}
          cy={y(hlName.number)}
          r={3}
          fill={colors[i]}
        />
        <text
          class="line-label name"
          x={x(xDomain[1])}
          y={y(hlName.number)}
          dx={6}
          dy={"0.35em"}
          fill={colors[i]}
        >
          {hlName.name + " " + hlName.number}
        </text>
        {#if hlName.data.find((d) => d.year === xDomain[0]) !== undefined}
          <circle
            cx={x(xDomain[0])}
            cy={y(hlName.data.find((d) => d.year === xDomain[0]).number)}
            r={3}
            fill={colors[i]}
          />
          <text
            class="line-label start"
            x={x(xDomain[0])}
            y={y(hlName.data.find((d) => d.year === xDomain[0]).number)}
            dx={-4}
            dy={"0.35em"}
            fill={colors[i]}
          >
            {hlName.data.find((d) => d.year === xDomain[0]).number}
          </text>
        {/if}
      {/if}
    {/each}
    {#if hoverData !== undefined}
      <path
        class={"hoverline"}
        id=""
        d={lineGenerator(hoverData.data)}
        fill="none"
        stroke={exploreGender === "girls" ? "#A332DE" : "#399D50"}
        stroke-width={3}
      />
      <circle
        cx={x(xDomain[1])}
        cy={y(hoverData.number)}
        r={3}
        fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
      />
      <text
        class="line-label name"
        x={x(xDomain[1])}
        y={y(hoverData.number)}
        dx={6}
        dy={"0.35em"}
        fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
      >
        {hoverData.name + " " + hoverData.number}
      </text>
      {#if hoverData.data.find((d) => d.year === xDomain[0]) !== undefined}
        <circle
          cx={x(xDomain[0])}
          cy={y(hoverData.data.find((d) => d.year === xDomain[0]).number)}
          r={3}
          fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
        />
        <text
          class="line-label start"
          x={x(xDomain[0])}
          y={y(hoverData.data.find((d) => d.year === xDomain[0]).number)}
          dx={-4}
          dy={"0.35em"}
          fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
        >
          {hoverData.data.find((d) => d.year === xDomain[0]).number}
        </text>
        {#if hoverPoint !== undefined}
          <circle
            cx={x(hoverPoint.year)}
            cy={y(hoverPoint.number)}
            r={4}
            fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
            stroke={"#ffffff"}
            stroke-width={1.5}
          />
          {#if hoverPoint.year !== 2000}
            <text
              class="line-label hover"
              x={x(hoverPoint.year)}
              y={y(hoverPoint.number)}
              fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
              dy={-6}>{hoverPoint.number}</text
            >
          {/if}
          <line
            x1={x(hoverPoint.year)}
            x2={x(hoverPoint.year)}
            y1={y(hoverPoint.number)}
            y2={y(0)}
            stroke-dasharray={"3 3"}
            stroke={exploreGender === "girls" ? "#A332DE" : "#399D50"}
            stroke-width={1}
          />
          <text
            class="line-label hover"
            x={x(hoverPoint.year)}
            y={y(0) + 2}
            fill={exploreGender === "girls" ? "#A332DE" : "#399D50"}
            dy={10}>{hoverPoint.year}</text
          >
        {/if}
      {/if}
    {/if}
  </g>
</svg>

<style>
  svg {
    overflow: visible;
  }
  .nameline {
    cursor: pointer;
  }
  .line-label {
    font-size: 12px;
    text-anchor: start;
    text-shadow: -2px -2px white, -2px 2px white, 2px 2px white, 2px -2px white,
      -2px 0 white, 0 2px white, 2px 0 white, 0 -2px white;
  }
  .line-label.name {
    font-weight: bold;
  }
  .line-label.start {
    font-weight: bold;
    text-anchor: end;
  }
  .line-label.hover {
    font-weight: bold;
    text-anchor: middle;
  }
  .hoverline,
  .highlight-line {
    pointer-events: none;
  }
  .axis-tick {
    text-anchor: middle;
    font-weight: normal;
    font-size: 12px;
    fill: #666;
  }
  .axis-x-tick {
    text-anchor: middle;
  }
  .axis-y-tick {
    text-anchor: end;
  }
</style>
